import { useParams, Link, Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { ArrowRight, ArrowLeft, CheckCircle2 } from 'lucide-react'
import { FEATURE_PAGES, type FeaturePageData } from '../data/featurePages'
import { useSeoHead, SITE } from '../lib/seo'
import { resolveFeatureSlug, featurePath } from '../lib/localizedUrl'
import fpEn from '../data/featurePages.en.json'
import fpDe from '../data/featurePages.de.json'
import fpPl from '../data/featurePages.pl.json'
import fpSk from '../data/featurePages.sk.json'
import fpIt from '../data/featurePages.it.json'
import fpHu from '../data/featurePages.hu.json'
import fpUk from '../data/featurePages.uk.json'
import fpEs from '../data/featurePages.es.json'
import fpFr from '../data/featurePages.fr.json'
import fpNl from '../data/featurePages.nl.json'
import fpJa from '../data/featurePages.ja.json'

// Překlady obsahu feature stránek — slug + ctaLink zůstávají z české předlohy.
const TRANS: Record<string, any[]> = { en: fpEn, de: fpDe, pl: fpPl, sk: fpSk, it: fpIt, hu: fpHu, uk: fpUk, es: fpEs, fr: fpFr, nl: fpNl, ja: fpJa }

export default function FeaturePage() {
  const { slug } = useParams()
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const lang = i18n.language || 'cs'
  // URL slug může být lokalizovaný i starý český → kanonický český slug.
  const czSlug = slug ? resolveFeatureSlug(slug, lang) : null

  const localize = (s: string): FeaturePageData | undefined => {
    const base = FEATURE_PAGES.find((x) => x.slug === s)
    if (!base) return undefined
    const tr = (TRANS[lang] || (lang !== 'cs' && TRANS.en) || []).find((x) => x.slug === s)
    return tr ? { ...base, ...tr } : base // tr nemá ctaLink → zůstává z base
  }

  const f = czSlug ? localize(czSlug) : undefined

  // Self-referencing canonical + hreflang per jazyk (lokalizované URL).
  const fUrl = f ? SITE + (lang === 'cs' ? '' : `/${lang}`) + featurePath(f.slug, lang) : ''
  useSeoHead({
    title: f ? `${f.title} | SEO Tools` : undefined,
    description: f?.subtitle,
    canonicalPath: f ? featurePath(f.slug, lang) : undefined,
    hreflangPath: f ? (l) => featurePath(f.slug, l) : undefined,
    jsonLd: f ? [
      {
        '@context': 'https://schema.org', '@type': 'TechArticle',
        headline: f.title, description: f.subtitle, inLanguage: lang, mainEntityOfPage: fUrl,
        author: { '@type': 'Organization', name: 'SEO Tools', url: SITE + '/' },
        publisher: { '@type': 'Organization', name: 'SEO Tools', url: SITE + '/' },
      },
      {
        '@context': 'https://schema.org', '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'SEO Tools', item: SITE + '/' },
          { '@type': 'ListItem', position: 2, name: f.title, item: fUrl },
        ],
      },
    ] : undefined,
  })

  if (!f) return <Navigate to="/" replace />
  // Přesměruj starou / nekanonickou URL na lokalizovanou.
  const want = featurePath(f.slug, lang)
  if (location.pathname !== want) return <Navigate to={want} replace />

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary-600 mb-6 transition">
        <ArrowLeft className="w-4 h-4" /> {t('feature.back')}
      </Link>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <div className="inline-flex items-center gap-2 bg-primary-50 text-primary-600 px-4 py-1.5 rounded-full text-xs font-semibold mb-4">
          {f.tag}
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">{f.title}</h1>
        <p className="text-lg text-gray-500 dark:text-gray-400 leading-relaxed">{f.subtitle}</p>
      </motion.div>

      <div className="mt-10 space-y-8">
        {f.sections.map((s, i) => (
          <section key={i}>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{s.h}</h2>
            {s.p?.map((para, j) => (
              <p key={j} className="text-[15px] leading-relaxed text-gray-700 dark:text-gray-300 mb-3">{para}</p>
            ))}
            {s.ul && (
              <ul className="space-y-2 mt-2">
                {s.ul.map((item, j) => (
                  <li key={j} className="flex items-start gap-2 text-[15px] text-gray-700 dark:text-gray-300">
                    <CheckCircle2 className="w-4 h-4 text-primary-500 mt-1 shrink-0" /> <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      {/* CTA */}
      <div className="mt-12 rounded-2xl bg-gradient-to-br from-primary-600 to-primary-500 p-8 text-center text-white">
        <h2 className="text-2xl font-bold mb-2">{f.ctaTitle}</h2>
        <p className="text-white/80 mb-5">{f.ctaText}</p>
        <Link to={f.ctaLink} className="inline-flex items-center gap-2 bg-white text-primary-700 px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition">
          {f.ctaButton} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Další funkce */}
      <div className="mt-12">
        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">{t('feature.more')}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FEATURE_PAGES.filter((x) => x.slug !== f.slug).map((x) => {
            const lx = localize(x.slug)!
            return (
              <Link key={x.slug} to={featurePath(x.slug, lang)}
                className="group flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm hover:border-primary-300 hover:bg-primary-50/40 dark:hover:bg-white/10 hover:shadow transition">
                <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{lx.title}</span>
                <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-primary-500 shrink-0 transition-colors" />
              </Link>
            )
          })}
        </div>
      </div>
    </div>
  )
}
